/**
 * CWOS canonical icon set (doc 03 §3.6). Line SVGs, 2px stroke, round
 * caps/joins, `currentColor`, on a 24×24 grid. These are the shared path
 * definitions; Admin renders them as `<svg>`, Mobile as react-native-svg
 * `<Path>`. No emoji in UI chrome.
 */
const ICONS = {
    // Navigation / shell
    today: { paths: ['M4 4h16v4H4z', 'M4 10h16v10H4z', 'M4 4v16M4 10h16M8 8v-4M16 8v-4'] },
    delivery: {
        paths: [
            'M3 7h11v10H3zM14 10h4l2 3v4h-6',
            'M6 20a2 2 0 100-4 2 2 0 000 4zM17 20a2 2 0 100-4 2 2 0 000 4z',
        ],
    },
    people: {
        paths: [
            'M8 11a3 3 0 100-6 3 3 0 000 6z',
            'M3 20c0-3 2-5 5-5s5 2 5 5',
            'M17 11a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
            'M16 15c3 0 5 2 5 5',
        ],
    },
    decisions: { paths: ['M9 3h6M9 3v3l4 6v6l-2 3h2l-2-3v-6L9 3z'] },
    trust: { paths: ['M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6z', 'M9 12l2 2 4-4'] },
    setup: {
        paths: [
            'M12 8a4 4 0 100 8 4 4 0 000-8z',
            'M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M19.1 4.9L17 7M7 17l-2.1 2.1',
        ],
    },
    messages: { paths: ['M4 5h16v11H8l-4 4z'] },
    handbook: { paths: ['M5 4h11a2 2 0 012 2v14H7a2 2 0 01-2-2z', 'M5 4v14a2 2 0 002 2h10'] },
    // Attention / severity
    bell: { paths: ['M6 9a6 6 0 0112 0c0 5 2 6 2 6H4s2-1 2-6', 'M10 19a2 2 0 004 0'] },
    dot: { paths: ['M12 12m-3 0a3 3 0 100-6 3 3 0 000 6z'], filled: true },
    check: { paths: ['M4 12l5 5L20 6'] },
    look: {
        paths: [
            'M12 17a5 5 0 100-10 5 5 0 000 10z',
            'M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4',
        ],
    },
    urgent: { paths: ['M12 3l10 17H2z', 'M12 9v5', 'M12 17v.5'] },
    unsure: {
        paths: [
            'M12 3a9 9 0 100 18 9 9 0 000-18z',
            'M9 9.5a3 3 0 015.2 1.8c0 1.5-2 2-2 3.7',
            'M12 17v.5',
        ],
    },
    // Chain links (EvidenceChain)
    planned: { paths: ['M8 7h8', 'M8 12h8', 'M8 17h8'] },
    clock: { paths: ['M12 3a9 9 0 100 18 9 9 0 000-18z', 'M12 7v5l3 3'] },
    delivered: { paths: ['M5 12l4 4L19 6'] },
    documented: { paths: ['M6 3h9l4 4v14H6z', 'M15 3v4h4', 'M9 13h6M9 17h6'] },
    trail: { paths: ['M4 18c4-8 12-8 16 0', 'M7 13c3-5 9-5 12 0', 'M10 8c2-3 5-3 7 0'] },
    // Worker shell (Waterline). Same grid, stroke and caps as everything above.
    home: { paths: ['M4 11l8-7 8 7', 'M6 9.5V20h12V9.5', 'M10 20v-5h4v5'] },
    /** The Tasks tab: checked rows, not a flag — a worker's queue is a list. */
    checklist: {
        paths: [
            'M4 5.5l1.5 1.5L8 4.5',
            'M4 11.5l1.5 1.5L8 10.5',
            'M4 17.5l1.5 1.5L8 16.5',
            'M11 6h9',
            'M11 12h9',
            'M11 18h9',
        ],
    },
    /** The Mileage tab: the worker's own car, not the delivery truck. */
    car: {
        paths: [
            'M4 16v-4l2-5h8l4 5h2v4h-2',
            'M4 12h14',
            'M7 18a2 2 0 100-4 2 2 0 000 4z',
            'M16 18a2 2 0 100-4 2 2 0 000 4z',
        ],
    },
    // Actions
    /** Summarized bars — the Dashboard's operating picture. */
    chart: { paths: ['M4 20h16', 'M7 20v-6', 'M12 20V9', 'M17 20V4'] },
    /** A trend line — Analytics, where the shape of the numbers is the point. */
    pulse: { paths: ['M3 12h4l3-7 4 14 3-7h4'] },
    /** One person — the viewer themselves. `people` is the team; this is you. */
    person: {
        paths: ['M12 4a4 4 0 100 8 4 4 0 000-8z', 'M5 20c1.4-3.8 3.9-5.5 7-5.5s5.6 1.7 7 5.5'],
    },
    search: { paths: ['M11 4a7 7 0 100 14 7 7 0 000-14z', 'M16 16l5 5'] },
    arrowRight: { paths: ['M4 12h16', 'M13 5l7 7-7 7'] },
    close: { paths: ['M6 6l12 12', 'M18 6L6 18'] },
    // Worker C.1 messaging + sync (back, send, channel kinds, muted).
    chevronLeft: { paths: ['M15 5l-7 7 7 7'] },
    send: { paths: ['M21 3L10 14', 'M21 3l-7 18-4-7-7-4z'] },
    hash: { paths: ['M5 9h15', 'M4 15h15', 'M10 3L8 21', 'M16 3l-2 18'] },
    megaphone: { paths: ['M3 10v4h4l7 5V5L7 10z', 'M18 9a4 4 0 010 6'] },
    bellOff: {
        paths: [
            'M8.7 4.4A6 6 0 0118 9c0 3 .7 4.6 1.3 5.4',
            'M17 17H4s2-1 2-6V9',
            'M10 19a2 2 0 004 0',
            'M3 3l18 18',
        ],
    },
    mapPin: {
        paths: ['M12 21s-7-6-7-11a7 7 0 0114 0c0 5-7 11-7 11z', 'M12 12a2 2 0 100-4 2 2 0 000 4z'],
    },
    lock: { paths: ['M6 11h12v9H6z', 'M8 11V7a4 4 0 018 0v4'] },
    /** Worker C.1 sync state: everything reached the server. */
    cloudCheck: {
        paths: ['M7 18a4.5 4.5 0 01-.5-9A6 6 0 0118 9.5a4.3 4.3 0 01-1 8.5z', 'M9.5 13.5l2 2 3.5-3.5'],
    },
    /** Worker C.1 sync state: held on this device, not yet sent. */
    phone: {
        paths: ['M8 3h8a1 1 0 011 1v16a1 1 0 01-1 1H8a1 1 0 01-1-1V4a1 1 0 011-1z', 'M11 18h2'],
    },
    plus: { paths: ['M12 5v14', 'M5 12h14'] },
    ellipsis: { paths: ['M6 12h.01M12 12h.01M18 12h.01'] },
    menu: { paths: ['M4 6h16', 'M4 12h16', 'M4 18h16'] },
    link: {
        paths: ['M9 15l6-6', 'M8 12l-3 3a3.5 3.5 0 005 5l3-3', 'M16 12l3-3a3.5 3.5 0 00-5-5l-3 3'],
    },
    refresh: {
        paths: ['M4 12a8 8 0 0114-5l2 2', 'M20 4v5h-5', 'M20 12a8 8 0 01-14 5l-2-2', 'M4 20v-5h5'],
    },
    /** A rule: one trigger, forking into the outcomes it produces. */
    automations: {
        paths: ['M3 12h4', 'M7 12V6h6', 'M7 12v6h6', 'M13 4h4v4h-4z', 'M13 16h4v4h-4z'],
    },
    /**
     * Ask Phibian. Two sparks rather than one: a single four-point star is a
     * rating, and the pair is what reads as "this surface is reasoning". Rationed
     * the same way `--gradient-intel` is — it belongs to the Intelligence surface
     * and nothing else may take it.
     */
    intelligence: {
        paths: [
            'M11 3.5c.7 4 1.8 5.1 5.8 5.8-4 .7-5.1 1.8-5.8 5.8-.7-4-1.8-5.1-5.8-5.8 4-.7 5.1-1.8 5.8-5.8z',
            'M18 14.5c.3 1.6.8 2.1 2.4 2.4-1.6.3-2.1.8-2.4 2.4-.3-1.6-.8-2.1-2.4-2.4 1.6-.3 2.1-.8 2.4-2.4z',
        ],
    },
};
export function getIcon(name) {
    return ICONS[name];
}
export const iconNames = Object.keys(ICONS);
//# sourceMappingURL=icons.js.map